import { useEffect, useRef } from 'react';

import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

import type { IntrinsicCameraParams } from './utils/rotationUtils';

type IntrinsicCameraProps = {
    /** Camera parameters (position, target, up) */
    params: IntrinsicCameraParams;
    /** Transition duration in seconds */
    transitionDuration?: number;
    /** Enable zoom via scroll/pinch */
    enableZoom?: boolean;
    /** Minimum zoom distance */
    minDistance?: number;
    /** Maximum zoom distance */
    maxDistance?: number;
};

// Easing function for smooth transitions
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

type CameraState = {
    position: THREE.Vector3;
    target: THREE.Vector3;
    up: THREE.Vector3;
    distance: number;
};

type CameraTransition = {
    isTransitioning: boolean;
    progress: number;
    startPosition: THREE.Vector3;
    startTarget: THREE.Vector3;
    startUp: THREE.Vector3;
    endPosition: THREE.Vector3;
    endTarget: THREE.Vector3;
    endUp: THREE.Vector3;
};

const updateTransition = (
    current: CameraState,
    transition: CameraTransition,
    delta: number,
    transitionDuration: number,
) => {
    if (!transition.isTransitioning) {
        return;
    }

    if (transitionDuration <= 0) {
        current.position.copy(transition.endPosition);
        current.target.copy(transition.endTarget);
        current.up.copy(transition.endUp);
        current.distance = current.position.distanceTo(current.target);
        transition.isTransitioning = false;
        return;
    }

    transition.progress += delta / transitionDuration;

    const progress = Math.min(transition.progress, 1);
    const easedProgress = easeOutCubic(progress);

    current.position.lerpVectors(transition.startPosition, transition.endPosition, easedProgress);
    current.target.lerpVectors(transition.startTarget, transition.endTarget, easedProgress);
    current.up.lerpVectors(transition.startUp, transition.endUp, easedProgress).normalize();
    current.distance = current.position.distanceTo(current.target);

    if (progress >= 1) {
        transition.isTransitioning = false;
    }
};

/**
 * Camera for intrinsic angle visualization.
 *
 * Unlike OrbitControls, this camera allows full control over orientation
 * including roll (via the up vector). This enables the reference direction
 * to appear horizontal on screen.
 *
 * Features:
 * - Smooth transitions when params change
 * - Zoom via scroll wheel (maintains orientation)
 * - No rotation control (orientation is locked to show angle correctly)
 */
export function IntrinsicCamera({
    params,
    transitionDuration = 0.5,
    enableZoom = true,
    minDistance = 0.5,
    maxDistance = 5,
}: IntrinsicCameraProps) {
    const camera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);

    // Current interpolated state
    const currentRef = useRef({
        position: params.position.clone(),
        target: params.target.clone(),
        up: params.up.clone(),
        distance: params.position.distanceTo(params.target),
    });

    // Transition state
    const transitionRef = useRef({
        isTransitioning: false,
        progress: 0,
        startPosition: new THREE.Vector3(),
        startTarget: new THREE.Vector3(),
        startUp: new THREE.Vector3(),
        endPosition: new THREE.Vector3(),
        endTarget: new THREE.Vector3(),
        endUp: new THREE.Vector3(),
    });

    // Track previous params to detect changes
    const prevParamsRef = useRef<IntrinsicCameraParams | null>(null);

    // Start transition when params change
    useEffect(() => {
        const prev = prevParamsRef.current;
        if (prev && !paramsEqual(prev, params)) {
            // Start a new transition
            const transition = transitionRef.current;
            const current = currentRef.current;

            transition.isTransitioning = true;
            transition.progress = 0;
            transition.startPosition.copy(current.position);
            transition.startTarget.copy(current.target);
            transition.startUp.copy(current.up);
            transition.endPosition.copy(params.position);
            transition.endTarget.copy(params.target);
            transition.endUp.copy(params.up);
        } else if (!prev) {
            // First render - snap to position
            currentRef.current.position.copy(params.position);
            currentRef.current.target.copy(params.target);
            currentRef.current.up.copy(params.up);
            currentRef.current.distance = params.position.distanceTo(params.target);
        }
        prevParamsRef.current = {
            position: params.position.clone(),
            target: params.target.clone(),
            up: params.up.clone(),
        };
    }, [params]);

    // Handle zoom via scroll wheel
    useEffect(() => {
        if (!enableZoom) return;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const current = currentRef.current;
            const zoomDelta = e.deltaY * 0.001;
            current.distance = THREE.MathUtils.clamp(
                current.distance + zoomDelta * current.distance,
                minDistance,
                maxDistance,
            );
        };

        gl.domElement.addEventListener('wheel', handleWheel, { passive: false });
        return () => gl.domElement.removeEventListener('wheel', handleWheel);
    }, [enableZoom, gl.domElement, minDistance, maxDistance]);

    // Update camera each frame
    useFrame((_, delta) => {
        const current = currentRef.current;

        updateTransition(current, transitionRef.current, delta, transitionDuration);

        // Compute final camera position (apply zoom distance)
        const direction = current.position.clone().sub(current.target).normalize();
        const finalPosition = current.target.clone().add(direction.multiplyScalar(current.distance));

        // Apply to camera
        camera.position.copy(finalPosition);
        camera.up.copy(current.up);
        camera.lookAt(current.target);
    });

    return null;
}

/**
 * Check if two IntrinsicCameraParams are equal (within tolerance)
 */
function paramsEqual(a: IntrinsicCameraParams, b: IntrinsicCameraParams, epsilon = 0.0001): boolean {
    return (
        a.position.distanceTo(b.position) < epsilon &&
        a.target.distanceTo(b.target) < epsilon &&
        a.up.distanceTo(b.up) < epsilon
    );
}
